import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../store/store';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import Fade from 'react-bootstrap/Fade';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import UserAvatar from '../UserAvatar';
import {
  FaQuoteRight, FaFlag, FaBullseye,
  FaPlus, FaTrash, FaEdit, FaCheck, FaTimes,
  FaPen, FaBell, FaLock, FaShareAlt, FaComment, FaComments, FaPaperPlane, FaSpinner, FaInfoCircle, FaCalendarAlt,
  FaGoogle, FaMicrosoft, FaApple,
} from 'react-icons/fa';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/light.css';
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';
import { getImage } from '../../utils/podcastClubUtils';
import KeyTakeawayImageWithPlaceholder from '../KeyTakeawayImageWithPlaceholder';
import TakeawayPractice from './TakeawayPractice';
import PracticeSkillsSelector from './PracticeSkillsSelector';
import httpService from '../../services/httpService';
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl, downloadIcsFile } from '../../utils/calendarUtils';
import '../../styles/PracticeModal.scss';

const DATETIME_OPTIONS = {
  enableTime: true,
  dateFormat: 'D, d M Y  H:i',
  time_24hr: true,
  closeOnSelect: false,
  plugins: [confirmDatePlugin({ confirmText: 'Close', theme: 'light' })],
};

const SECTIONS = [
  {
    key: 'quote',
    label: 'Quote',
    icon: <FaQuoteRight style={{ fontSize: '0.65rem' }} />,
  },
  {
    key: 'insight',
    label: 'Insight',
    icon: <FaFlag style={{ fontSize: '0.65rem' }} />,
  },
  {
    key: 'implementation',
    label: 'One way to try this',
    icon: <FaBullseye style={{ fontSize: '0.65rem' }} />,
  },
];

/* ─── Dates Tab ─────────────────────────────────────────────────────────────── */
const DatesTab = ({ practiceId, currentPractice, takeaway }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [notifForm, setNotifForm]         = useState({ type: 'email', date: null });
  const [addingNotif, setAddingNotif]     = useState(false);
  const [savingNotif, setSavingNotif]     = useState(false);
  const [editingId, setEditingId]         = useState(null);
  const [editForm, setEditForm]           = useState({ type: 'email', date: null });
  const [savingEdit, setSavingEdit]       = useState(false);

  useEffect(() => {
    if (!practiceId) return;
    setLoading(true);
    httpService.get(`/dash/practices/${practiceId}/notifications`)
      .then(res => setNotifications(res.data.data || []))
      .catch(err => console.error('Error loading notifications:', err))
      .finally(() => setLoading(false));
  }, [practiceId]);

  const addNotification = () => {
    if (!notifForm.date) return;
    setSavingNotif(true);
    httpService.post(`/dash/practices/${practiceId}/notifications`, {
      type: notifForm.type,
      date: new Date(notifForm.date).toISOString(),
    })
      .then(res => {
        setNotifications(prev => [...prev, res.data.data]);
        setNotifForm({ type: 'email', date: null });
        setAddingNotif(false);
      })
      .catch(err => console.error('Error adding notification:', err))
      .finally(() => setSavingNotif(false));
  };

  const startEdit = (notif) => {
    setEditingId(notif.id);
    setEditForm({ type: notif.type, date: notif.date ? new Date(notif.date) : null });
  };

  const saveEdit = () => {
    if (!editForm.date) return;
    setSavingEdit(true);
    httpService.put(`/dash/practices/${practiceId}/notifications/${editingId}`, {
      type: editForm.type,
      date: new Date(editForm.date).toISOString(),
    })
      .then(res => {
        setNotifications(prev => prev.map(n => n.id === editingId ? res.data.data : n));
        setEditingId(null);
      })
      .catch(err => console.error('Error updating notification:', err))
      .finally(() => setSavingEdit(false));
  };

  const deleteNotification = (id) => {
    httpService.delete(`/dash/practices/${practiceId}/notifications/${id}`)
      .then(() => setNotifications(prev => prev.filter(n => n.id !== id)))
      .catch(err => console.error('Error deleting notification:', err));
  };

  return (
    <div className="pt-3">
      {/* Scheduled date */}
      {currentPractice?.practice_date_time && (
        <div className="mb-3 p-2 rounded d-flex align-items-center gap-2" style={{ background: 'rgba(0,0,0,0.04)', fontSize: '0.82rem' }}>
          <FaBell style={{ opacity: 0.45, fontSize: '0.75rem' }} />
          <span className="text-muted">Scheduled:</span>
          <strong>
            {new Date(currentPractice.practice_date_time).toLocaleString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric',
              hour: 'numeric', minute: '2-digit', hour12: true,
            })}
          </strong>
        </div>
      )}

      {/* Add to calendar */}
      {currentPractice?.practice_date_time && (() => {
        const start = new Date(currentPractice.practice_date_time);
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        const calendarEvent = {
          title: currentPractice?.title
            || (takeaway?.title ? `Practice: ${takeaway.title}` : 'Practice'),
          description: [
            takeaway?.title && `Insight: ${takeaway.title}`,
            currentPractice?.body,
          ].filter(Boolean).join('\n\n'),
          start,
          end,
        };
        return (
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>Add to calendar:</span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => window.open(buildGoogleCalendarUrl(calendarEvent), '_blank', 'noopener,noreferrer')}
            >
              <FaGoogle className="me-1" />Google
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => window.open(buildOutlookCalendarUrl(calendarEvent), '_blank', 'noopener,noreferrer')}
            >
              <FaMicrosoft className="me-1" />Outlook
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => downloadIcsFile(calendarEvent)}
            >
              <FaApple className="me-1" />Apple
            </Button>
          </div>
        );
      })()}

      {/* Add notification */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold text-muted" style={{ fontSize: '0.8rem' }}>Email Reminders</span>
        {!currentPractice?.completed && (
          <Button size="sm" variant="outline-primary" onClick={() => setAddingNotif(v => !v)}>
            <FaPlus className="me-1" />Add
          </Button>
        )}
      </div>

      <Collapse in={addingNotif}>
        <div>
          <div className="p-2 mb-3 border rounded bg-light">
            <Row className="g-2 align-items-end">
              <Col sm={4}>
                <Form.Select size="sm" value={notifForm.type}
                  onChange={e => setNotifForm(p => ({ ...p, type: e.target.value }))}>
                  <option value="email">Email</option>
                  <option disabled value="text">Text</option>
                </Form.Select>
              </Col>
              <Col sm={5}>
                <Flatpickr
                  className="form-control form-control-sm"
                  value={notifForm.date}
                  options={DATETIME_OPTIONS}
                  onChange={([date]) => setNotifForm(p => ({ ...p, date: date || null }))}
                  placeholder="Select date & time"
                />
              </Col>
              <Col sm={3} className="d-flex gap-1">
                <Button size="sm" variant="outline-success" onClick={addNotification}
                  disabled={savingNotif || !notifForm.date}><FaCheck /></Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setAddingNotif(false)}>
                  <FaTimes />
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </Collapse>

      {loading && <small className="text-muted">Loading…</small>}
      {!loading && notifications.length === 0 && (
        <div className="text-center py-3 text-muted" style={{ fontSize: '0.82rem' }}>No reminders set.</div>
      )}

      {notifications.map(notif => (
        <div key={notif.id} className="pm-notif-item">
          <div className="pm-notif-item__row">
            <div className="pm-notif-item__info">
              <Badge bg={notif.type === 'email' ? 'primary' : 'success'}
                style={{ fontSize: '0.65rem' }}>
                {notif.type}
              </Badge>
              <span className="pm-notif-item__date">
                {new Date(notif.date).toLocaleString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit', hour12: true,
                })}
              </span>
            </div>
            {!currentPractice?.completed && (
              <div className="d-flex gap-1">
                <Button size="sm" variant="outline-secondary" onClick={() => startEdit(notif)}>
                  <FaEdit />
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => deleteNotification(notif.id)}>
                  <FaTrash />
                </Button>
              </div>
            )}
          </div>
          <Collapse in={editingId === notif.id}>
            <div>
              <Row className="g-2 align-items-end mt-2">
                <Col sm={4}>
                  <Form.Select size="sm" value={editForm.type}
                    onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}>
                    <option value="email">Email</option>
                    <option disabled value="text">Text</option>
                  </Form.Select>
                </Col>
                <Col sm={5}>
                  <Flatpickr
                    className="form-control form-control-sm"
                    value={editForm.date}
                    options={DATETIME_OPTIONS}
                    onChange={([date]) => setEditForm(p => ({ ...p, date: date || null }))}
                  />
                </Col>
                <Col sm={3} className="d-flex gap-1">
                  <Button size="sm" variant="outline-success" onClick={saveEdit}
                    disabled={savingEdit || !editForm.date}><FaCheck /></Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setEditingId(null)}>
                    <FaTimes />
                  </Button>
                </Col>
              </Row>
            </div>
          </Collapse>
        </div>
      ))}
    </div>
  );
};

/* ─── Completion Section ────────────────────────────────────────────────────── */
const CompletionSection = ({ keyTakeawayId, currentPractice, onPracticeUpdated }) => {
  const [form, setForm] = useState({
    completed: currentPractice?.completed || false,
    completion_date_time: currentPractice?.completion_date_time
      ? new Date(currentPractice.completion_date_time) : null,
    reflection: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      completed: currentPractice?.completed || false,
      completion_date_time: currentPractice?.completion_date_time
        ? new Date(currentPractice.completion_date_time) : null,
      reflection: '',
    });
  }, [currentPractice?.id]);

  const handleCompletedChange = (e) => {
    const checked = e.target.checked;
    setForm(prev => ({
      ...prev,
      completed: checked,
      completion_date_time: checked && !prev.completion_date_time ? new Date() : prev.completion_date_time,
    }));
  };

  const save = () => {
    setSaving(true);
    httpService.put(`/dash/key-takeaway/${keyTakeawayId}/practices/${currentPractice.id}`, {
      completed: form.completed,
      completion_date_time: form.completion_date_time
        ? new Date(form.completion_date_time).toISOString() : null,
    })
      .then(res => {
        const updated = res.data.data;
        if (form.reflection.trim()) {
          return httpService.post(`/comments/${currentPractice.id}`, {
            body: form.reflection.trim(),
            type: 'completion',
          }).then(() => {
            setForm(prev => ({ ...prev, reflection: '' }));
            onPracticeUpdated?.(updated);
          });
        }
        onPracticeUpdated?.(updated);
      })
      .catch(err => console.error('Error saving completion:', err))
      .finally(() => setSaving(false));
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <Form.Group className="mb-2 d-flex align-items-center gap-3">
        <Form.Check
          type="switch"
          id="practice-completed-switch"
          label="Mark as completed"
          checked={form.completed}
          onChange={handleCompletedChange}
        />
        {form.completed && <Badge bg="success" style={{ fontSize: '0.7rem' }}>✓ Completed</Badge>}
      </Form.Group>
      <Collapse in={form.completed}>
        <div>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem' }}>Completion date &amp; time</Form.Label>
            <div className="input-group">
              <Flatpickr
                className="form-control border-success"
                value={form.completion_date_time || undefined}
                options={DATETIME_OPTIONS}
                onChange={([date]) => setForm(prev => ({ ...prev, completion_date_time: date || null }))}
                placeholder="Select completion date & time…"
              />
              <span className="input-group-text border-success"><FaCalendarAlt /></span>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem' }}>Completion reflection</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.reflection}
              onChange={e => setForm(prev => ({ ...prev, reflection: e.target.value }))}
              placeholder="How did it go? What did you learn or notice?"
            />
          </Form.Group>
          <div className="d-flex align-items-center gap-3">
            <p className="mb-0 text-muted" style={{ fontSize: '0.75rem', flex: 1 }}>
              Once marked complete, you won't be able to edit your plan or change skills. You'll still be able to add reflections, comments, and share this practice with your podcaster or other listeners in this club.
            </p>
            <Button variant="success" size="md" onClick={save} disabled={saving || form.reflection.trim().length < 3}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/* ─── Settings Tab ──────────────────────────────────────────────────────────── */
const SettingsTab = ({ keyTakeawayId, currentPractice, takeaway, onPracticeUpdated }) => {
  const user = useAuthStore(s => s.user);
  const [form, setForm] = useState({
    shared_for_review: !!currentPractice?.shared_for_review,
    is_public: !!currentPractice?.is_public,
  });
  const [saving, setSaving]       = useState(false);
  const [comments, setComments]   = useState([]);

  useEffect(() => {
    setForm({
      shared_for_review: !!currentPractice?.shared_for_review,
      is_public: !!currentPractice?.is_public,
    });
  }, [currentPractice?.id]);

  useEffect(() => {
    if (!currentPractice?.id) return;
    httpService.get(`/comments/${currentPractice.id}`)
      .then(res => setComments(res.data.comments || []))
      .catch(() => {});
  }, [currentPractice?.id]);

  const save = () => {
    setSaving(true);
    httpService.put(`/dash/key-takeaway/${keyTakeawayId}/practices/${currentPractice.id}`, {
      shared_for_review: form.shared_for_review ? new Date().toISOString() : null,
      is_public: form.is_public,
    })
      .then(res => onPracticeUpdated?.(res.data.data))
      .catch(err => console.error('Error saving settings:', err))
      .finally(() => setSaving(false));
  };

  const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="pt-3">
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="practice-shared-for-review-switch"
          label="Send to Podcaster for review"
          checked={form.shared_for_review}
          onChange={e => setForm(prev => ({ ...prev, shared_for_review: e.target.checked }))}
        />
        <Form.Text className="text-muted">Allow the podcaster to see this practice.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Check
          type="switch"
          id="practice-is-public-switch"
          label="Share with the Club"
          checked={form.is_public}
          onChange={e => setForm(prev => ({ ...prev, is_public: e.target.checked }))}
        />
        <Form.Text className="text-muted">Show this practice to other listeners.</Form.Text>
      </Form.Group>
      <Button variant="outline-success" size="md" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save Settings'}
      </Button>

      {/* ── Preview ── */}
      <hr className="my-4" />
      <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
        This is how your practice will appear when shared. You can unshare it at any time by toggling the options above.
      </p>
      <Card className="shadow-sm">
        <Card.Body>
          {/* User row */}
          <Row className="justify-content-center align-items-center mb-2">
            <Col xs="auto">
              <UserAvatar user={user} size={40} />
            </Col>
            <Col className="ps-1">
              <div className="fw-semibold" style={{ fontSize: '0.88rem', lineHeight: 1.2 }}>
                {user?.first_name}
              </div>
              <small className="text-muted">{currentPractice?.created_at ? fmt(currentPractice.created_at) : '—'}</small>
            </Col>
          </Row>

          {/* Takeaway title */}
          {takeaway?.title && (
            <div className="mb-2 text-center" style={{ fontSize: '0.78rem' }}>
              <span className="fw-semibold text-dark">{takeaway.title}</span>
            </div>
          )}

          {/* Practice title + body */}
          {(currentPractice?.title || currentPractice?.body) && (
            <div className="mb-2 text-center">
              {currentPractice.title && (
                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{currentPractice.title}</div>
              )}
              {currentPractice.body && (
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                  {currentPractice.body.length > 100
                    ? currentPractice.body.slice(0, 100) + '…'
                    : currentPractice.body}
                </div>
              )}
            </div>
          )}

          {/* Badges */}
          <Row className="justify-content-center g-1 mb-3">
            <Col xs="auto">
              <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                <FaComments className="me-1" />
                {comments.length} reflection{comments.length !== 1 ? 's' : ''}
              </Badge>
            </Col>
            {currentPractice?.completed && (
              <Col xs="auto">
                <Badge bg="success" style={{ fontSize: '0.7rem' }}>Completed</Badge>
              </Col>
            )}
          </Row>

          {/* Reflections */}
          {comments.length > 0 && (
            <div className="border-top pt-3">
              <small className="text-muted d-block mb-2 fw-semibold" style={{ fontSize: '0.75rem' }}>Reflections</small>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {comments.map(c => (
                  <div key={c.id} className="d-flex gap-2 align-items-start">
                    <UserAvatar user={c.user} size={26} />
                    <div style={{ flex: 1 }}>
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <span className="fw-semibold" style={{ fontSize: '0.78rem' }}>
                          {c.user?.first_name} {c.user?.last_name}
                        </span>
                        {c.type === 'completion' && (
                          <Badge bg="success" style={{ fontSize: '0.6rem' }}>Completion Reflection</Badge>
                        )}
                        {c.is_podcaster && (
                          <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }}>Podcaster</Badge>
                        )}
                        <small className="text-muted ms-auto" style={{ fontSize: '0.7rem' }}>{fmt(c.created_at)}</small>
                      </div>
                      <p className="mb-0" style={{ fontSize: '0.8rem' }}>{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

/* ─── Comments Tab ──────────────────────────────────────────────────────────── */
const CommentsTab = ({ practiceId }) => {
  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [body, setBody]             = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [editBody, setEditBody]     = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!practiceId) return;
    setLoading(true);
    httpService.get(`/comments/${practiceId}`)
      .then(res => setComments(res.data.comments || []))
      .catch(err => console.error('Error loading reflections:', err))
      .finally(() => setLoading(false));
  }, [practiceId]);

  const submitComment = () => {
    if (!body.trim()) return;
    setSubmitting(true);
    httpService.post(`/comments/${practiceId}`, { body })
      .then(res => {
        setComments(prev => [...prev, res.data.comment]);
        setBody('');
      })
      .catch(err => console.error('Error posting comment:', err))
      .finally(() => setSubmitting(false));
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const saveEdit = () => {
    setSavingEdit(true);
    httpService.put(`/comments/${practiceId}/${editingId}`, { body: editBody })
      .then(res => {
        setComments(prev => prev.map(c => c.id === editingId ? res.data.comment : c));
        setEditingId(null);
      })
      .catch(err => console.error('Error editing comment:', err))
      .finally(() => setSavingEdit(false));
  };

  const deleteComment = (id) => {
    httpService.delete(`/comments/${practiceId}/${id}`)
      .then(() => setComments(prev => prev.filter(c => c.id !== id)))
      .catch(err => console.error('Error deleting comment:', err));
  };

  return (
    <div className="pt-3">
      {loading && <small className="text-muted">Loading…</small>}

      <div className="mb-3" style={{ maxHeight: '280px', overflowY: 'auto' }}>
        {!loading && comments.length === 0 && (
          <div className="text-center py-3 text-muted" style={{ fontSize: '0.82rem' }}>No reflections yet.</div>
        )}
        {comments.map(comment => (
          <div
            key={comment.id}
            className={`pm-comment ${comment.is_podcaster ? 'pm-comment--podcaster' : 'pm-comment--listener'}`}
          >
            <div className="pm-comment__header">
              {comment.user?.avatar_url && (
                <img
                  src={comment.user.avatar_url}
                  alt=""
                  className="pm-comment__avatar"
                />
              )}
              <span className="pm-comment__name">
                {comment.user?.first_name} {comment.user?.last_name}
              </span>
              {comment.type === 'completion' && (
                <Badge bg="success" style={{ fontSize: '0.62rem' }}>Completion Reflection</Badge>
              )}
              {comment.is_podcaster && (
                <Badge bg="warning" text="dark" style={{ fontSize: '0.62rem' }}>Podcaster</Badge>
              )}
              <span className="pm-comment__date">
                {new Date(comment.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                })}
              </span>
              <div className="d-flex gap-1 ms-auto">
                <Button size="sm" variant="outline-warning" onClick={() => startEdit(comment)}>
                  <FaEdit size={11} />
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => deleteComment(comment.id)}>
                  <FaTrash size={11} />
                </Button>
              </div>
            </div>
            <p className="pm-comment__body">{comment.body}</p>
            <Collapse in={editingId === comment.id}>
              <div>
                <div className="d-flex gap-1 mt-2">
                  <Form.Control
                    size="sm" as="textarea" rows={2}
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                  />
                  <div className="d-flex flex-column gap-1">
                    <Button size="sm" variant="outline-success" onClick={saveEdit} disabled={savingEdit}>
                      <FaCheck />
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEditingId(null)}>
                      <FaTimes />
                    </Button>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        ))}
      </div>

      {/* Compose */}
      <div className="d-flex gap-0 align-items-center">
        <Form.Control
          as="textarea" rows={2} size="sm"
          placeholder="Write a reflection…"
          value={body}
          onChange={e => setBody(e.target.value)}
          className={'me-3'}
        />
        <OverlayTrigger placement="top" overlay={<Tooltip>Post reflection</Tooltip>}>
          <span>
            <Button
              variant="primary" size="sm"
              onClick={submitComment}
              disabled={submitting || !body.trim()}
            >
              {submitting ? <FaSpinner className="fa-spin" /> : <FaPaperPlane />}
            </Button>
          </span>
        </OverlayTrigger>
      </div>
    </div>
  );
};

/* ─── Create-mode Skill Picker ──────────────────────────────────────────────── */
const CreateModeSkillPicker = ({ podcastClubGuid, selectedIds, onChange }) => {
  const [skills, setSkills] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);

  useEffect(() => {
    if (!podcastClubGuid) return;
    httpService.get(`/dash/club/${podcastClubGuid}/skills`)
      .then(res => setSkills(res.data.skills || []))
      .catch(err => console.error('Error fetching skills:', err));
  }, [podcastClubGuid]);

  if (!podcastClubGuid) return null;

  const toggle = (id) =>
    onChange(selectedIds.includes(id)
      ? selectedIds.filter(s => s !== id)
      : [...selectedIds, id]);

  const handleAddCustomSkill = () => {
    if (!newTitle.trim()) return;
    setSavingCustom(true);
    httpService.post(`/dash/club/${podcastClubGuid}/listener-skills`, {
      title: newTitle.trim(),
      description: newDesc.trim() || undefined,
    })
      .then(res => {
        const skill = res.data.skill;
        setSkills(prev => [...prev, skill]);
        onChange([...selectedIds, skill.id]);
        setNewTitle('');
        setNewDesc('');
        setShowAddForm(false);
      })
      .catch(err => console.error('Error creating custom skill:', err))
      .finally(() => setSavingCustom(false));
  };

  return (
    <div className="mt-3">
      <small className="text-muted d-block mb-2">What do you think it will help you with?<br /><small className="text-muted">Tag this practice against the skills it works on. Pick one or several. This is what makes it count toward what you're getting better at across the club.</small></small>
      <div className="d-flex flex-wrap gap-2">
        {skills.map(skill => {
          const selected = selectedIds.includes(skill.id);
          return (
            <div key={skill.id} className={`skill-tile${selected ? ' skill-tile--attached' : ''}${skill.is_custom ? ' skill-tile--custom' : ''}`}>
              {skill.is_custom && <span className="skill-tile__custom-badge">Your skill</span>}
              <div className="skill-tile__title">{skill.title}</div>
              {skill.description && <div className="skill-tile__desc">{skill.description}</div>}
              <button
                type="button"
                className={`skill-tile__btn skill-tile__btn--${selected ? 'detach' : 'attach'}`}
                onClick={() => toggle(skill.id)}
              >
                {selected ? <FaTimes /> : <FaPlus />}
                {selected ? 'Remove' : 'Add'}
              </button>
            </div>
          );
        })}
      </div>
      <div className="skill-custom-info">
        <FaLock style={{ fontSize: '0.6rem' }} />
        Custom skills are private to you — other listeners won't see them.
      </div>
      <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => setShowAddForm(v => !v)}>
        <FaPlus className="me-1" />Add your own skill
      </Button>
      <Collapse in={showAddForm}>
        <div>
          <div className="skill-add-form mt-2">
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                type="text"
                placeholder="Skill title (required)"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                as="textarea"
                rows={2}
                placeholder="Description (optional)"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={handleAddCustomSkill}
                disabled={savingCustom || !newTitle.trim()}
              >
                {savingCustom ? <FaSpinner className="fa-spin me-1" /> : null}
                Save Skill
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/* ─── Main Modal ────────────────────────────────────────────────────────────── */
const PracticeModal = ({
  show,
  onHide,
  practice_id,
  takeaway: takeawayProp,
  practice: initialPractice,
  keyTakeawayId: keyTakeawayIdProp,
  podcastClub: podcastClubProp,
  onPracticeChange,
  onPracticeDeleted,
}) => {
  const [currentPractice, setCurrentPractice]     = useState(initialPractice || null);
  const [isEditMode, setIsEditMode]               = useState(false);
  const [saving, setSaving]                       = useState(false);
  const [deleting, setDeleting]                   = useState(false);
  const [showContent, setShowContent]             = useState(true);
  const [selectedSkillIds, setSelectedSkillIds]   = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loadingPractice, setLoadingPractice]     = useState(false);
  const [commentsKey, setCommentsKey]             = useState(0);
  const [derived, setDerived]                     = useState({ takeaway: null, keyTakeawayId: null, podcastClub: null });
  const [infoVisible, setInfoVisible]      = useState(false);
  const [activeTab, setActiveTab]          = useState('content');
  const practiceRef = useRef(null);

  const takeaway    = takeawayProp     ?? derived.takeaway;
  const keyTakeawayId = keyTakeawayIdProp ?? derived.keyTakeawayId;
  const podcastClub = podcastClubProp  ?? derived.podcastClub;

  useEffect(() => {
    if (!show) return;
    setIsEditMode(false);
    setShowContent(true);
    setSelectedSkillIds([]);
    setShowDeleteConfirm(false);
    setActiveTab('content');

    if (practice_id && !initialPractice) {
      setCurrentPractice(null);
      setDerived({ takeaway: null, keyTakeawayId: null, podcastClub: null });
      setLoadingPractice(true);
      httpService.get(`/dash/practices/${practice_id}`)
        .then(res => {
          const p = res.data.data;
          setCurrentPractice(p);
          setDerived({
            takeaway:      p.key_takeaway ?? null,
            keyTakeawayId: p.podcast_episode_key_takeaway_id ?? null,
            podcastClub:   p.key_takeaway?.podcast_club ?? null,
          });
        })
        .catch(err => console.error('Error loading practice:', err))
        .finally(() => setLoadingPractice(false));
    } else {
      setCurrentPractice(initialPractice || null);
      setDerived({ takeaway: null, keyTakeawayId: null, podcastClub: null });
    }
  }, [show, practice_id]);

  const isCreateMode = !currentPractice?.id;

  const handlePracticeSaved = (savedPractice) => {
    const wasCreate = isCreateMode;
    setCurrentPractice(savedPractice);
    onPracticeChange?.(savedPractice, wasCreate);
    setShowContent(false);
    setTimeout(() => {
      setIsEditMode(false);
      setTimeout(() => setShowContent(true), 50);
    }, 300);
    // Newly created practices land on Reminders, so the reminder gets set
    // right away instead of requiring a second trip back into the modal.
    if (wasCreate) setActiveTab('dates');
  };

  const openEditMode = () => {
    setShowContent(false);
    setTimeout(() => {
      setIsEditMode(true);
      setTimeout(() => setShowContent(true), 50);
    }, 300);
  };

  const cancelEdit = () => {
    setShowContent(false);
    setTimeout(() => {
      setIsEditMode(false);
      setTimeout(() => setShowContent(true), 50);
    }, 300);
  };

  const deletePractice = () => {
    if (!keyTakeawayId || !currentPractice?.id) return;
    setDeleting(true);
    setShowDeleteConfirm(false);
    httpService.delete(`/dash/key-takeaway/${keyTakeawayId}/practices/${currentPractice.id}`)
      .then(() => {
        onPracticeDeleted?.(currentPractice.id);
        onHide();
      })
      .catch(err => console.error('Error deleting practice:', err))
      .finally(() => setDeleting(false));
  };

  const handlePracticeUpdated = (updated) => {
    setCurrentPractice(updated);
    onPracticeChange?.(updated, false);
  };

  const reloadCurrentPractice = () => {
    if (!currentPractice?.id) return;
    setLoadingPractice(true);
    httpService.get(`/dash/practices/${currentPractice.id}`)
      .then(res => {
        const p = res.data.data;
        setCurrentPractice(p);
        onPracticeChange?.(p, false);
        setCommentsKey(k => k + 1);
      })
      .catch(err => console.error('Error reloading practice:', err))
      .finally(() => setLoadingPractice(false));
  };

  /* ── Derived display values ── */
  const modeLabel = isCreateMode ? 'New Practice'
    : isEditMode ? 'Editing Practice'
    : 'Practice';

  const heroTitle = isCreateMode
    ? (takeaway?.title || 'New Practice')
    : (currentPractice?.title || takeaway?.title || 'Practice');

  const heroImg = takeaway?.latest_image?.image_url
    || getImage(podcastClub, 'wide')
    || null;

  /* ═══════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════ */
  return (
    <Modal size="lg" show={show} onHide={onHide} className="pm-modal">

      {/* ── Hero header (plain div — avoids Bootstrap .modal-header interference) */}
      <div
        className={`pm-hero ${!heroImg ? 'pm-hero--no-image' : ''}`}
        style={heroImg ? { backgroundImage: `url(${heroImg})` } : undefined}
      >
        <div className="pm-hero__scrim">
          <button
            type="button"
            className="pm-hero__info-btn"
            onClick={() => setInfoVisible(prev => !prev)}
            aria-label="Toggle info"
          >
            <FaInfoCircle />
          </button>
          <button
            type="button"
            className="btn-close btn-close-white pm-hero__close"
            onClick={onHide}
            aria-label="Close"
          />
          <div className="pm-hero__kicker">
            {modeLabel}
            {podcastClub?.title && <> · {podcastClub.title}</>}
          </div>
          <h5 className="pm-hero__title">{heroTitle}</h5>
          {!isCreateMode && currentPractice && (
            <span className={`pm-hero__status ${currentPractice.completed ? 'pm-hero__status--done' : ''}`}>
              {currentPractice.completed ? '✓ Completed' : '◉ In Progress'}
            </span>
          )}
        </div>
      </div>

      <Collapse in={infoVisible}>
        <div>
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src="https://www.loom.com/embed/3f43f8adbd744c3ea1b2db21f5c3db31"
              frameBorder="0"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              title="Practice info"
            />
          </div>
        </div>
      </Collapse>

      {/* ── Body ──────────────────────────────────── */}
      <Modal.Body>
        {loadingPractice && (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <p className="mt-2 text-muted small">Loading practice…</p>
          </div>
        )}

        {!loadingPractice && (
          <Row className="g-3">

            {/* Left: Insight context (always visible) */}
            <Col lg={4}>
              {/* Takeaway image */}
              <div className="pm-insight__img">
                <KeyTakeawayImageWithPlaceholder image={takeaway?.latest_image} />
              </div>
              <Collapse class="m-0 p-0" in={infoVisible}>
                <div class="m-0 p-0">
                  <small className="d-block">What the host pulled out of the audio for this moment. The quote is the source, the insight is the idea, and the last card is one concrete way someone could try it.<br/><br/></small>
                </div>
              </Collapse>
              {/* Insight sections — no collapse, all visible */}
              {SECTIONS.filter(({ key }) => takeaway?.[key]).map(({ key, label, icon }) => (
                <div key={key} className={`pm-insight__section pm-insight__section--${key}`}>
                  <div className="pm-insight__label">
                    {icon}
                    {label}
                  </div>
                  <p className="pm-insight__text">{takeaway?.[key]}</p>
                </div>
              ))}
            </Col>

            {/* Right: Tabbed practice content */}
            <Col lg={8}>
              {isCreateMode && (
                <p className="small text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                  <FaLock className="me-1" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                  <strong>Reflections</strong>, <strong>Reminders</strong> and <strong>Privacy</strong> tabs will be unlocked once you save your practice.
                </p>
              )}
              <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-2">
                {/* ── Your Plan ── */}
                <Tab
                  eventKey="content"
                  title={<span><FaPen className="me-1" style={{ fontSize: '0.72rem' }} />Your Plan</span>}
                >
                  <Fade in={showContent} timeout={300}>
                    <div className="pt-2">
                      <TakeawayPractice
                        ref={practiceRef}
                        practice={currentPractice || {}}
                        keyTakeawayId={keyTakeawayId}
                        isEditing={isCreateMode || isEditMode}
                        onSaved={handlePracticeSaved}
                        onSavingChange={setSaving}
                      />
                      {!isCreateMode && !isEditMode && currentPractice && !currentPractice.completed && (
                        <CompletionSection
                          keyTakeawayId={keyTakeawayId}
                          currentPractice={currentPractice}
                          onPracticeUpdated={reloadCurrentPractice}
                        />
                      )}
                      {isCreateMode && (
                        <CreateModeSkillPicker
                          podcastClubGuid={podcastClub?.guid}
                          selectedIds={selectedSkillIds}
                          onChange={setSelectedSkillIds}
                        />
                      )}
                      {!isCreateMode && !isEditMode && currentPractice && (
                        <PracticeSkillsSelector
                          podcastClubGuid={podcastClub?.guid}
                          keyTakeawayId={keyTakeawayId}
                          practice={currentPractice}
                          isCompleted={!!currentPractice.completed}
                          onPracticeUpdated={handlePracticeUpdated}
                        />
                      )}
                    </div>
                  </Fade>
                </Tab>

                {/* ── Reflections ── */}
                <Tab
                  eventKey="comments"
                  title={
                    <span style={{ opacity: isCreateMode ? 0.38 : 1 }}>
                      <FaComment className="me-1" style={{ fontSize: '0.72rem' }} />Reflections
                    </span>
                  }
                  disabled={isCreateMode}
                >
                  {!isCreateMode && currentPractice?.id && (
                    <CommentsTab key={commentsKey} practiceId={currentPractice.id} />
                  )}
                </Tab>

                {/* ── Reminders ── */}
                <Tab
                  eventKey="dates"
                  title={
                    <span style={{ opacity: isCreateMode ? 0.38 : 1 }}>
                      <FaBell className="me-1" style={{ fontSize: '0.72rem' }} />Reminders
                    </span>
                  }
                  disabled={isCreateMode}
                >
                  {!isCreateMode && currentPractice?.id && (
                    <DatesTab practiceId={currentPractice.id} currentPractice={currentPractice} takeaway={takeaway} />
                  )}
                </Tab>

                {/* ── Sharing ── */}
                <Tab
                  eventKey="settings"
                  title={
                    <span style={{ opacity: isCreateMode ? 0.38 : 1 }}>
                      <FaShareAlt className="me-1" style={{ fontSize: '0.72rem' }} />Sharing
                    </span>
                  }
                  disabled={isCreateMode}
                >
                  {!isCreateMode && currentPractice?.id && (
                    <SettingsTab
                      keyTakeawayId={keyTakeawayId}
                      currentPractice={currentPractice}
                      takeaway={takeaway}
                      onPracticeUpdated={handlePracticeUpdated}
                    />
                  )}
                </Tab>

              </Tabs>
            </Col>

          </Row>
        )}
      </Modal.Body>

      {/* ── Footer ────────────────────────────────── */}
      <Modal.Footer>
        <div className="pm-footer__main">
          {/* Left: destructive action */}
          <div>
            {!isCreateMode && !isEditMode && currentPractice && (
              <Button
                variant="link"
                className="text-danger p-0 pm-footer__delete"
                onClick={() => setShowDeleteConfirm(v => !v)}
                disabled={deleting}
                style={{ fontSize: '0.82rem', textDecoration: 'none' }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>

          {/* Right: primary actions */}
          <div className="pm-footer__right">
            {isEditMode && (
              <Button variant="link" className="text-muted p-0 me-1" onClick={cancelEdit}
                style={{ fontSize: '0.85rem', textDecoration: 'none' }}>
                Cancel
              </Button>
            )}
            {!isCreateMode && !isEditMode && currentPractice && !currentPractice.completed && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Edit practice</Tooltip>}>
                <Button variant="outline-secondary" size="sm" onClick={openEditMode}>
                  <FaPen style={{ fontSize: '0.72rem' }} />
                </Button>
              </OverlayTrigger>
            )}
            <Button variant="outline-secondary" size="sm" onClick={onHide}>
              Close
            </Button>
            {isCreateMode && (
              <Button
                size="sm"
                onClick={() => practiceRef.current?.save({ skillIds: selectedSkillIds })}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Practice'}
              </Button>
            )}
            {isEditMode && (
              <Button
                size="sm"
                onClick={() => practiceRef.current?.save()}
                disabled={saving}
              >
                {saving ? 'Updating…' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>

        {/* Delete confirm strip */}
        <Collapse in={showDeleteConfirm}>
          <div>
            <div className="pm-footer__delete-confirm">
              <small className="text-muted me-auto">Delete this practice permanently?</small>
              <Button size="sm" variant="danger" onClick={deletePractice} disabled={deleting}>
                Yes, delete
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Collapse>
      </Modal.Footer>

    </Modal>
  );
};

export default PracticeModal;
